import {Component, Inject, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {MAT_DIALOG_DATA, MatDialog} from '@angular/material/dialog';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {BasicPersonDetailDTO} from '../../../../ent/models/person/BasicPersonDetailDTO';
import {SharedService} from '../../../services/shared-service';
import {DateUtil} from '../../../util/DateUtil';
import {TableControl} from '../../../models/TableControl';
import {ClmRequestBuilder} from '../../../../clm/services/clm-request-builder';
import {CaseStatus} from '../../../../clm/types';
import {PagedRequestDTO} from '../../../models/PagedRequestDTO';
import {ClaimDisplayDTO} from '../../../../clm/models/claim/ClaimDisplayDTO';
import {ClaimService} from '../../../../clm/services/claim.service';
import {getSortDirection} from '../../../util/Util';
import {PagedFilterDTO} from '../../../models/PagedFilterDTO';
import {ClaimType} from 'src/app/modules/common/types';
import {ClmFunctions} from 'src/app/modules/clm/common/functions/clm.functions';
import {detailsExpand} from '../../../animations';
import {EntityTable} from '../../../types/entity-table.enum';
import {ClaimMedschemeResponseDTO} from 'src/app/modules/clm/models/claim/ClaimMedschemeResponseDTO';
import {ClaimMedschemeDTO} from 'src/app/modules/clm/models/claim/ClaimMedschemeDTO';
import {ClaimPhaResponseDTO} from 'src/app/modules/clm/models/claim/ClaimPhaResponseDTO';
import {Admin} from '../../../types/portal.enum';
import {CustomDateRange} from '../../../types/common.types';

enum TableColumns {
  TransactionId = 'transactionId',
  TransactionType = 'transactionType',
  ProviderName = 'providerName',
  ReceiveDate = 'receiveDate',
  EncounterStartDate = 'encounterStartDate', // column -> encounter date
  AssessingStatus = 'assessingStatus',
  PayTo = 'payTo',
  AmountClaimed = 'amount',
  BenefitAmount = 'benefitAmount',
}

@Component({
  selector: 'app-transaction-overview',
  templateUrl: './transactions-overview.component.html',
  styleUrls: [
    '../../../../../../styles/global/_management-styles.scss',
    '../../../../../../styles/global/_dialog-styles.scss',
    './transactions-overview.component.scss'
  ],
  animations: [detailsExpand]
})
export class TransactionsOverviewComponent implements OnInit, OnDestroy {
  readonly destroyed$ = new Subject();
  readonly dateFormat = DateUtil.dateFormat;
  readonly caseStatus = CaseStatus;
  readonly controls = ClmRequestBuilder.getTableControls();
  readonly ClaimType = ClaimType;
  readonly ClmFunctions = ClmFunctions;
  readonly tableColumns = TableColumns;

  @Input() policyNumber: string;

  transactionsDataSource = new MatTableDataSource<ClaimMedschemeDTO | ClaimDisplayDTO>;
  transactionsTableControl: TableControl;
  transactionsDisplayedColumns: string[] = Object.values(TableColumns);
  adminStatus: Admin = Admin.Pha;
  dateRange: CustomDateRange;
  isSearchDisabled: boolean = false;

  @ViewChild('transactionsPaginator', {static: false}) transactionsPaginator: MatPaginator;

  @ViewChild('MatSortTransactionStep') set matSort(ms: MatSort) {
    if (!ms) {
      return;
    }
    this.transactionsDataSource.sortingDataAccessor = (item: ClaimDisplayDTO, property: string) => {
      if (typeof item[property] === 'object' && item[property] !== null) {
        return item[property].name;
      }
      else {
        return item[property];
      }
    };
    this.transactionsDataSource.sort = ms;
  }

  constructor(
    public dialog: MatDialog,
    private claimService: ClaimService,
    private sharedService: SharedService,
    @Inject(MAT_DIALOG_DATA) public basicPersonDetails: BasicPersonDetailDTO) {

    this.transactionsTableControl = new TableControl(
      this.controls.length,
      this.controls.pageSize,
      this.controls.pageNumber,
      this.controls.pageSizeOptions,
      'transactionId',
      'desc',
      '',
      '');
  }

  get isDataExist(): boolean {
    return !!(this.transactionsDataSource && this.transactionsDataSource.data?.length);
  }

  ngOnInit(): void {
    this.setDefaultDate();
    this.getListOfTransactions(this.transactionsTableControl);
  }

  setDefaultDate(): void {
    const date = new Date();
    const FIRST_DAY_OF_MONTH = 1;
    this.dateRange = {
      start: new Date(date.getFullYear(), date.getMonth(), FIRST_DAY_OF_MONTH + 1),
      end: date
    };
  }

  searchByDate(): void {
    this.getListOfTransactions(this.transactionsTableControl);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  onDateRangeChanged(dateRange: CustomDateRange): void {
    if (dateRange) {
      this.isSearchDisabled = false;
    }
    else {
      this.isSearchDisabled = true;
    }
    this.dateRange = dateRange;
  }

  pageChanged($event: PageEvent): void {
    this.transactionsTableControl.pageNumber = $event.pageIndex;
    this.transactionsTableControl.pageSize = $event.pageSize;
    this.getListOfTransactions(this.transactionsTableControl);
  }

  private getListOfTransactions(tableControl: TableControl): void {

    if (!this.policyNumber) {
      return;
    }

    if (!this.dateRange?.start && !this.dateRange?.end){
      this.setDefaultDate();
    }

    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblTransaction;
    pagedRequestDTO.pageNumber = tableControl.pageNumber;
    pagedRequestDTO.pageSize = tableControl.pageSize;
    pagedRequestDTO.sortField = tableControl.sortField;
    pagedRequestDTO.sortDirection = getSortDirection(tableControl.sortDirection);
    pagedRequestDTO.fieldMap = {};


    const personPagedFilterDTO = new PagedFilterDTO();
    personPagedFilterDTO.name = 'insuredPersonByInsuredPersonId.insuredPersonId';
    personPagedFilterDTO.value = String(this.basicPersonDetails.personId);
    pagedRequestDTO.fieldMap[`insuredPersonByInsuredPersonId_1`] = personPagedFilterDTO;

    const membershipFilterDTO = new PagedFilterDTO();
    membershipFilterDTO.name = 'membershipNumber';
    membershipFilterDTO.value = this.policyNumber;
    pagedRequestDTO.fieldMap[`membershipNumber`] = membershipFilterDTO;

    const startDateDTO = new PagedFilterDTO();
    startDateDTO.name = 'startDate';
    startDateDTO.value = (this.dateRange?.start?.toISOString() || null);
    pagedRequestDTO.fieldMap[`startDate`] = startDateDTO;

    const endDateDTO = new PagedFilterDTO();
    endDateDTO.name = 'endDate';
    endDateDTO.value = (this.dateRange?.end?.toISOString() || null);
    pagedRequestDTO.fieldMap[`endDate`] = endDateDTO;

    this.claimService.getListOfTransactions(pagedRequestDTO, this.basicPersonDetails.optionCode)
      .pipe(
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (response: ClaimMedschemeResponseDTO | ClaimPhaResponseDTO) => {
          this.transactionsDataSource.data = response.pagedQueryResultDTO.resultList;
          this.transactionsTableControl.length = response.pagedQueryResultDTO.totalRecords;
          this.adminStatus = response.adminStatus;
        },
        error: (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      });
  }
}

